import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Typography,
  Paper,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
} from '@mui/material';
import {
  Security,
  ToggleOn,
  ToggleOff,
  Search,
  CheckCircle,
  Cancel,
  CheckBoxOutlineBlank,
  CheckBox,
} from '@mui/icons-material';
import UserApiService from 'api/UserApiService';
import mfaApiService from 'api/MfaApiService';
import { useTranslation } from 'react-i18next';
import { useAppState } from 'store/Provider';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import { useSelector } from 'store/hooks';
import User from 'types/entities/User';
import useApi from 'hooks/useApi';
import './AdminMfaManagement.scss';

const AdminMfaManagement: React.FC = () => {
  const { dispatch } = useAppState();
  const loadings = useSelector((state) => state.loadings);
  const [t] = useTranslation();
  const { makeCall } = useApi();

  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState<'enable' | 'disable'>('enable');
  const [processing, setProcessing] = useState(false);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Filter states
  const [clientTypeFilter, setClientTypeFilter] = useState<'all' | 'internal' | 'external'>('all');

  // Batch selection states
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [showBatchConfirmDialog, setShowBatchConfirmDialog] = useState(false);
  const [batchActionType, setBatchActionType] = useState<'enable' | 'disable'>('enable');

  useEffect(() => {
    loadUsers(page, rowsPerPage);
  }, [page, rowsPerPage]);

  useEffect(() => {
    // Reset to first page when searching or filtering
    if (page !== 0) {
      setPage(0);
    } else {
      loadUsers(0, rowsPerPage);
    }
  }, [searchTerm, clientTypeFilter]);

  const loadUsers = async (currentPage: number, pageSize: number) => {
    try {
      dispatch(addLoadingAction('loadUsers'));

      // Build query params with search term if provided
      const queryParams: any = {
        size: pageSize,
        page: currentPage,
      };

      if (searchTerm) {
        queryParams['last_name,first_name,mail'] = `:${searchTerm}:`;
      }

      // Add client type filter
      if (clientTypeFilter !== 'all') {
        queryParams.is_client = clientTypeFilter === 'external' ? 1 : 0;
      }

      const response = await makeCall(UserApiService.get(queryParams), 'Unable to retrieve users');

      if (response && response.datas) {
        setUsers(response.datas);
        setTotalItems(response.totalItems || 0);
      } else {
        setUsers([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
      setTotalItems(0);
    } finally {
      dispatch(removeLoadingAction('loadUsers'));
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleMfa = (user: User, action: 'enable' | 'disable') => {
    setSelectedUser(user);
    setActionType(action);
    setShowConfirmDialog(true);
  };

  const handleConfirmToggle = async () => {
    if (!selectedUser) return;

    try {
      setProcessing(true);
      const enabled = actionType === 'enable';

      await mfaApiService.adminSetUserMfa(selectedUser.id, enabled);

      // If enabling MFA for the current logged-in user, clear their session storage
      // so they will be prompted to set up MFA
      if (enabled) {
        sessionStorage.removeItem('mfa_check_done');
        sessionStorage.removeItem('mfa_check_result');
      }

      // Update local state
      setUsers(
        users.map((u) =>
          u.id === selectedUser.id
            ? { ...u, mfa_enabled: enabled, mfa_preference: enabled ? 'SOFTWARE_TOKEN_MFA' : null }
            : u
        )
      );

      const userName = `${selectedUser.first_name} ${selectedUser.last_name}`;
      const successMessage = enabled
        ? `MFA has been enabled for ${userName}. User will be prompted to set up MFA on next login.`
        : `MFA has been disabled for ${userName}`;

      dispatch(
        setSnackbarAction({
          open: true,
          message: successMessage,
          severity: 'success',
        })
      );

      setShowConfirmDialog(false);
      setSelectedUser(null);
    } catch (error: any) {
      dispatch(
        setSnackbarAction({
          open: true,
          message: error.response?.data?.message || `Failed to ${actionType} MFA`,
          severity: 'error',
        })
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelToggle = () => {
    setShowConfirmDialog(false);
    setSelectedUser(null);
  };

  // Batch operations
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const eligibleUserIds = users
        .filter((user) => user.cognito_username) // Only users with Cognito accounts
        .map((user) => user.id);
      setSelectedUserIds(eligibleUserIds);
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSelectUser = (userId: number) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      }
      return [...prev, userId];
    });
  };

  const handleBatchAction = (action: 'enable' | 'disable') => {
    if (selectedUserIds.length === 0) {
      dispatch(
        setSnackbarAction({
          open: true,
          message: 'Please select at least one user',
          severity: 'warning',
        })
      );
      return;
    }
    setBatchActionType(action);
    setShowBatchConfirmDialog(true);
  };

  const handleConfirmBatchAction = async () => {
    try {
      setProcessing(true);
      const enabled = batchActionType === 'enable';

      // Process all selected users
      const promises = selectedUserIds.map((userId) => mfaApiService.adminSetUserMfa(userId, enabled));

      await Promise.all(promises);

      // If enabling MFA, clear session storage
      if (enabled) {
        sessionStorage.removeItem('mfa_check_done');
        sessionStorage.removeItem('mfa_check_result');
      }

      // Update local state for all selected users
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          selectedUserIds.includes(user.id)
            ? {
                ...user,
                mfa_enabled: enabled,
                mfa_preference: enabled ? 'SOFTWARE_TOKEN_MFA' : null,
                mfa_setup_completed: enabled ? false : user.mfa_setup_completed,
              }
            : user
        )
      );

      dispatch(
        setSnackbarAction({
          open: true,
          message: `MFA has been ${enabled ? 'enabled' : 'disabled'} for ${selectedUserIds.length} user(s)`,
          severity: 'success',
        })
      );

      setShowBatchConfirmDialog(false);
      setSelectedUserIds([]);
    } catch (error: any) {
      dispatch(
        setSnackbarAction({
          open: true,
          message: error.response?.data?.message || `Failed to ${batchActionType} MFA for selected users`,
          severity: 'error',
        })
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelBatchAction = () => {
    setShowBatchConfirmDialog(false);
  };

  const eligibleUsersCount = users.filter((u) => u.cognito_username).length;
  const isAllSelected = eligibleUsersCount > 0 && selectedUserIds.length === eligibleUsersCount;
  const isSomeSelected = selectedUserIds.length > 0 && selectedUserIds.length < eligibleUsersCount;

  return (
    <Box className="admin-mfa-container">
      <Paper className="admin-mfa-paper">
        <Box className="admin-mfa-header">
          <Box className="admin-mfa-title">
            <Security className="admin-mfa-icon" />
            <Typography variant="h5">Multi-Factor Authentication Management</Typography>
          </Box>
          <Box className="admin-mfa-controls">
            <FormControl size="small" className="admin-mfa-filter">
              <InputLabel>Client Type</InputLabel>
              <Select
                value={clientTypeFilter}
                label="Client Type"
                onChange={(e) => setClientTypeFilter(e.target.value as 'all' | 'internal' | 'external')}
              >
                <MenuItem value="all">All Clients</MenuItem>
                <MenuItem value="internal">Internal Only</MenuItem>
                <MenuItem value="external">External Only</MenuItem>
              </Select>
            </FormControl>
            <TextField
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              className="admin-mfa-search"
            />
          </Box>
        </Box>

        {/* Batch Action Toolbar */}
        {selectedUserIds.length > 0 && (
          <Box className="admin-mfa-batch-toolbar">
            <Typography variant="subtitle1" className="admin-mfa-batch-selected">
              {selectedUserIds.length} user(s) selected
            </Typography>
            <Box className="admin-mfa-batch-actions">
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => handleBatchAction('enable')}
                startIcon={<ToggleOn />}
              >
                Enable MFA
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => handleBatchAction('disable')}
                startIcon={<ToggleOff />}
              >
                Disable MFA
              </Button>
              <Button variant="outlined" size="small" onClick={() => setSelectedUserIds([])}>
                Clear Selection
              </Button>
            </Box>
          </Box>
        )}

        {loadings.loadUsers ? (
          <Box className="admin-mfa-loading">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={isSomeSelected}
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      disabled={eligibleUsersCount === 0}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="center">Client Type</TableCell>
                  <TableCell align="center">MFA Status</TableCell>
                  <TableCell align="center">Setup Completed</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} selected={selectedUserIds.includes(user.id)} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => handleSelectUser(user.id)}
                          disabled={!user.cognito_username}
                        />
                      </TableCell>
                      <TableCell>
                        {user.last_name} {user.first_name}
                      </TableCell>
                      <TableCell>{user.mail}</TableCell>
                      <TableCell align="center">
                        {!user.is_client ? (
                          <Chip label="Internal" color="info" size="small" />
                        ) : (
                          <Chip label="External" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {user.mfa_enabled ? (
                          <Chip icon={<CheckCircle />} label="Enabled" color="success" size="small" />
                        ) : (
                          <Chip icon={<Cancel />} label="Disabled" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {user.mfa_setup_completed ? (
                          <Chip label="Yes" color="primary" size="small" />
                        ) : (
                          <Chip label="No" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {user.mfa_enabled ? (
                          <Tooltip title="Disable MFA">
                            <IconButton color="error" size="small" onClick={() => handleToggleMfa(user, 'disable')}>
                              <ToggleOff />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Enable MFA">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleToggleMfa(user, 'enable')}
                              disabled={!user.cognito_username}
                            >
                              <ToggleOn />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={handleCancelToggle}>
        <DialogTitle>{actionType === 'enable' ? 'Enable' : 'Disable'} MFA for User?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {actionType} MFA for{' '}
            <strong>
              {selectedUser?.first_name} {selectedUser?.last_name}
            </strong>
            ?
          </Typography>
          {actionType === 'enable' && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: '10px' }}>
              Note: The user will need to set up their authenticator app the next time they log in.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelToggle} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmToggle}
            color={actionType === 'enable' ? 'primary' : 'error'}
            variant="contained"
            disabled={processing}
          >
            {processing ? <CircularProgress size={24} /> : `${actionType === 'enable' ? 'Enable' : 'Disable'} MFA`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Confirmation Dialog */}
      <Dialog open={showBatchConfirmDialog} onClose={handleCancelBatchAction}>
        <DialogTitle>{batchActionType === 'enable' ? 'Enable' : 'Disable'} MFA for Multiple Users?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {batchActionType} MFA for{' '}
            <strong>{selectedUserIds.length} selected user(s)</strong>?
          </Typography>
          {batchActionType === 'enable' && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: '10px' }}>
              Note: All selected users will need to set up their authenticator app the next time they log in.
            </Typography>
          )}
          {batchActionType === 'disable' && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: '10px' }}>
              Note: This will disable MFA for all selected users immediately.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelBatchAction} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmBatchAction}
            color={batchActionType === 'enable' ? 'primary' : 'error'}
            variant="contained"
            disabled={processing}
          >
            {processing ? (
              <CircularProgress size={24} />
            ) : (
              `${batchActionType === 'enable' ? 'Enable' : 'Disable'} MFA for ${selectedUserIds.length} User(s)`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminMfaManagement;
